// @flow
import React, { useContext, useState } from 'react';
import { useDispatch } from 'react-redux';
import { confirm, showAlert } from 'actions/app';
import { HTTPStatusCodes } from 'constants/httpStatusCodes';
import { useFeatureFlags } from 'providers/FeatureFlagsProvider';
import { UserProviderContext } from 'providers/UserProvider';
import API from 'services/API';
import { userHasRole } from 'services/Authorization';
import strings from 'strings';
import type { ProfileDataTableProps } from 'types/app';
import { When } from 'UI/components/atoms/When';
import { ActivityDrawer } from 'UI/components/organisms/ActivityDrawer';
import { componentDimensions } from 'UI/constants/dimensions';
import { TabKeys } from 'UI/constants/entityTypes';
import { FeatureFlags } from 'UI/constants/featureFlags';
import { Roles } from 'UI/constants/roles';
import { SvgNoActivities } from 'UI/res/icons/milano';
import { getErrorMessage } from 'UI/utils/index';

import { extraNoteRenderers } from '../constants';
import ProfileTableLayout from '../ProfileTableLayout';

import { getFinalActivityColumns, opsCanManage } from './activity.utils';
import { useStyles } from './styles';

type ActivityTabProps = {
  DataTableProps: ProfileDataTableProps,
  endpoint: string,
  isQuickViewMode: boolean,
  tablePreferencesKey: string,
  profileId: string,
  profileModule: string
};

const {
  inventoryProfiles: {
    sections: {
      tabs: {
        activityNote: { activityActionText }
      }
    }
  }
} = strings;
const { emptyState } = componentDimensions;

const ActivityTab = ({
  DataTableProps,
  endpoint,
  isQuickViewMode = false,
  profileId,
  profileModule,
  tablePreferencesKey
}: ActivityTabProps) => {
  const classes = useStyles();
  const dispatch = useDispatch();

  const [uiState, setUiState] = useState({
    isDrawerOpen: false,
    selectedItem: null,
    shouldRefreshTableData: false,
    id: null,
    entityId: null,
    entity: profileModule
  });

  const [user] = useContext(UserProviderContext);
  const { checkIfFeatureFlagEnabled } = useFeatureFlags();

  const initialCols = getFinalActivityColumns(profileModule);

  const areOutcomeFieldsEnabled = checkIfFeatureFlagEnabled(
    FeatureFlags.ActivityOutcomeFieldsInForm
  );

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   A C T I O N S
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const openDrawer = () => {
    setUiState(prev => ({
      ...prev,
      isDrawerOpen: true,
      shouldRefreshTableData: false,
      entityId: profileId
    }));
  };

  const handleEditNoteClick = (item: any) => {
    setUiState(prev => ({
      ...prev,
      id: item.id,
      isDrawerOpen: true,
      selectedItem: item,
      entityId: profileId,
      shouldRefreshTableData: false
    }));
  };

  const closeDrawer = () => {
    setUiState(prev => ({
      ...prev,
      id: null,
      isDrawerOpen: false,
      entityId: null,
      entity: profileModule,
      selectedItem: null
    }));
  };

  const handleDeleteClick = async (itemId: number, getData) =>
    dispatch(
      confirm({
        severity: 'warning',
        title: strings.inventoryProfiles.sections.tabs.activityNote.deleteAction.title,
        message: strings.inventoryProfiles.sections.tabs.activityNote.message,
        ...strings.shared.ui.keepDeleteButtonCopies,
        onConfirm: async ok => {
          if (!ok) return;

          try {
            const response = await API.delete(`${endpoint}/activityLogs/${itemId}`);
            if (response.status === HTTPStatusCodes.Ok) {
              await getData();
              dispatch(
                showAlert({
                  severity: 'success',
                  title: strings.inventoryProfiles.sections.tabs.activityNote.deletedActivity
                })
              );
            }
          } catch (error) {
            dispatch(
              showAlert({
                severity: 'error',
                title: 'Error',
                body: getErrorMessage(error)
              })
            );
          }
        }
      })
    );

  const handleOnSubmitActivitySucced = () =>
    setUiState(prev => ({ ...prev, shouldRefreshTableData: true }));

  /** ::::::::::::::::::::::::::::::::::::::::::::::::::::
   *
   *   C O L U M N S   C O N F I G
   *
   * :::::::::::::::::::::::::::::::::::::::::::::::::::::
   */

  const isOperationsUser = userHasRole(Roles.Operations, user);

  const activityActions = [
    { name: 'view', callback: handleEditNoteClick },
    {
      name: 'edit',
      callback: handleEditNoteClick,
      isVisible: isOperationsUser,
      checkIfShouldBeEnabled: () => areOutcomeFieldsEnabled
    },
    {
      name: 'delete',
      callback: handleDeleteClick,
      pickId: true,
      isVisible: isOperationsUser,
      checkIfShouldBeEnabled: ({ rowData }, tabData, initialColumns) =>
        opsCanManage({ rowData }, tabData, initialColumns, isOperationsUser)
    }
  ];

  const tabEmptyState = <SvgNoActivities size={emptyState.width} />;

  return (
    <>
      <ProfileTableLayout
        actionText={activityActionText}
        className={classes.activityNoteTable}
        customEmptyState={tabEmptyState}
        customName="created_by_system"
        DataTableProps={DataTableProps}
        extraRenderers={extraNoteRenderers}
        finalActions={activityActions}
        hasProfileLoaded={profileId}
        initialColumns={initialCols}
        isQuickViewMode={isQuickViewMode}
        onNewItemClick={openDrawer}
        orderByOptions={{ column: 'updated_at', direction: 'desc' }}
        profileModule={profileModule}
        shouldRefresh={uiState.shouldRefreshTableData}
        tabKey={TabKeys.ActivityLogs}
        tablePreferencesKey={tablePreferencesKey}
      />

      <When condition={uiState.isDrawerOpen}>
        <ActivityDrawer
          activity={uiState}
          entity={profileModule}
          open={uiState.isDrawerOpen}
          onClose={closeDrawer}
          onSubmitSucceed={handleOnSubmitActivitySucced}
        />
      </When>
    </>
  );
};

export default ActivityTab;
